import Keycloak from 'keycloak-js';
import { setAuthCookies, clearAuthCookies } from '../shared/utils/cookieUtils';

const TOKEN_REFRESH_SECONDS = 240; // must be less than token expiration time (currently 300s)
const REFRESH_INTERVAL_MS = 4 * 60 * 1000; // proactive refresh every 4 minutes
const TOKEN_REQUEST_FAILURE_KEY = 'affilio.auth.token-request-failed';

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
});

let keycloakInitPromise: Promise<boolean> | null = null;
let refreshIntervalId: number | null = null;
let beforeUnloadHandler: (() => void) | null = null;
let sessionStopInProgress = false;

function readTokenRequestFailureFlag(): boolean {
  try {
    return sessionStorage.getItem(TOKEN_REQUEST_FAILURE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeTokenRequestFailureFlag(): void {
  try {
    sessionStorage.setItem(TOKEN_REQUEST_FAILURE_KEY, '1');
  } catch {
    // sessionStorage can throw in private mode; in-memory flags still stop this page load.
  }
}

function clearTokenRequestFailureFlag(): void {
  try {
    sessionStorage.removeItem(TOKEN_REQUEST_FAILURE_KEY);
  } catch {
    // ignore
  }
}

export function hasTokenRequestFailed(): boolean {
  return readTokenRequestFailureFlag() || sessionStopInProgress;
}

function stopRefreshInterval(): void {
  if (refreshIntervalId !== null) {
    window.clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

function disableAutoLogin(): void {
  // keycloak-js clearToken() calls login() when loginRequired is true.
  Object.assign(keycloak, { loginRequired: false });
}

function markTokenRequestFailed(): void {
  writeTokenRequestFailureFlag();
  stopRefreshInterval();
  disableAutoLogin();
}

export async function stopSessionAfterTokenFailure(): Promise<void> {
  if (sessionStopInProgress) {
    return;
  }

  sessionStopInProgress = true;
  markTokenRequestFailed();
  clearAuthCookies();

  if (!keycloak.authenticated) {
    return;
  }

  try {
    await keycloak.logout({ redirectUri: window.location.origin });
  } catch {
    // Stay on this page; App shows the auth error because the failure flag is set.
  }
}

const refreshTokenOrLogout = () => {
  if (hasTokenRequestFailed()) {
    return;
  }

  keycloak.updateToken(TOKEN_REFRESH_SECONDS).catch(() => {
    void stopSessionAfterTokenFailure();
  });
};

const handlePostInit = (authenticated: boolean) => {
  if (authenticated) {
    clearTokenRequestFailureFlag();
    sessionStopInProgress = false;

    stopRefreshInterval();
    refreshIntervalId = window.setInterval(refreshTokenOrLogout, REFRESH_INTERVAL_MS);

    if (beforeUnloadHandler) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
    beforeUnloadHandler = () => {
      stopRefreshInterval();
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userUuid = keycloak.idTokenParsed?.sub;

    if (userUuid) {
      setAuthCookies({
        userUuid,
        username: keycloak.tokenParsed?.preferred_username,
        givenName: keycloak.tokenParsed?.given_name,
        familyName: keycloak.tokenParsed?.family_name,
      });
    }
    return;
  }

  if (!hasTokenRequestFailed()) {
    keycloak.login();
  }
};

export const initKeycloak = () => {
  if (!keycloakInitPromise) {
    const skipAutoLogin = hasTokenRequestFailed();

    keycloakInitPromise = keycloak.init({
      ...(skipAutoLogin ? {} : { onLoad: 'login-required' as const }),
      checkLoginIframe: false,
      enableLogging: true,
      flow: 'standard',
    });
    keycloakInitPromise = keycloakInitPromise
      .then((authenticated) => {
        handlePostInit(authenticated);
        return authenticated;
      })
      .catch((error) => {
        markTokenRequestFailed();
        throw error;
      });
  }

  return keycloakInitPromise;
};

export const retryLogin = () => {
  sessionStopInProgress = false;
  clearTokenRequestFailureFlag();
  keycloak.login();
};

// Start auth as soon as this module is imported
initKeycloak();

keycloak.onAuthError = () => {
  markTokenRequestFailed();
};

keycloak.onAuthRefreshError = () => {
  void stopSessionAfterTokenFailure();
};

keycloak.onTokenExpired = () => {
  refreshTokenOrLogout();
};

export const handleLogout = async () => {
  sessionStopInProgress = true;
  stopRefreshInterval();
  disableAutoLogin();
  clearTokenRequestFailureFlag();
  try {
    clearAuthCookies();
    await keycloak.logout();
  } catch {
    clearAuthCookies();
  }
};

export { keycloak };
